import React, { FunctionComponent, ReactElement } from 'react';
import {
  MemberFragment,
  MeFragment,
  useRemoveUserMutation,
  MyRole,
  UserRole,
} from '$/GraphQL/schema/schema';
import ApolloForm from '$/components/ApolloForm';
import { TEAM_USERS_REFETCH } from '$/GraphQL/refetchQueries';
import { AdminRoles } from '$/sections/Home/Profile/TeamManager/TeamManager';
import { css } from '@emotion/react';
import { GET_SETS_STATUS } from '$/GraphQL/queries/EWebinar';
import Dropdown from '$/components/ui/Input/Basics/Dropdown';
import Modal from '$/components/ui/Modal';
import Form from '$/components/ui/Form';
import Button from '$/components/ui/Button';

export interface RemoveModalProps {
  me: MeFragment;
  member: MemberFragment;
  users: MemberFragment[];
  onClose: () => void;
}

export interface RemoveUserInput {
  id: string;
  replacementId: string;
}

export interface ReplacementUserProps extends RemoveModalProps {
  onRemoveUser: (data: RemoveUserInput) => Promise<void>;
}

interface Option {
  text: string;
  value: string;
}

export const SelectReplacementUser: FunctionComponent<ReplacementUserProps> = ({
  me,
  member,
  users,
  onClose,
  onRemoveUser,
}) => {
  const defaultValues: RemoveUserInput = {
    id: member.id,
    replacementId: me.id,
  };

  const replacementUsers = users.reduce((filtered: Option[], user) => {
    if (
      user.id !== member.id &&
      (member.role === UserRole.Presenter || user.role !== UserRole.Presenter)
    ) {
      const option = { value: user.id, text: [user.firstName, user.lastName].join(' ') };
      filtered.push(option);
    }
    return filtered;
  }, []);

  return (
    <ApolloForm
      className='w-full'
      onSubmit={async (values): Promise<void> => {
        await onRemoveUser(values);
      }}
      defaultValues={defaultValues}
    >
      <Modal.Header delete>
        <Modal.Title>
          <span>
            Remove{' '}
            <span className='font-semibold'>{[member.firstName, member.lastName].join(' ')}</span>{' '}
            from the team?
          </span>
        </Modal.Title>
        <Modal.Close onClick={onClose} />
      </Modal.Header>
      <Modal.Body>
        <div className='flex flex-wrap h-full'>
          <Form.Field name='id' component={<input />} className='hidden' type='hidden' />
          <p>
            Please select a new team member that will take over moderator responsibilities and
            replace them as presenter for any existing eWebinars.
          </p>
          <Form.FieldLabel large title='Select a replacement team member:' />
          <Form.Field
            component={<Dropdown options={replacementUsers} placeholder='Select member' />}
            name='replacementId'
            className='w-full mb-0'
          />
          <Form.ErrorMessage />
        </div>
      </Modal.Body>
      <Modal.Footer sticky={false}>
        <div className='w-full flex justify-between'>
          <Button.cancel
            onClick={(): void => {
              onClose();
            }}
          >
            Don't remove
          </Button.cancel>
          <Form.SubmitButton className='px-8 py-2 bg-red-1'>Remove</Form.SubmitButton>
        </div>
      </Modal.Footer>
    </ApolloForm>
  );
};

export const WarningYourSelf: FunctionComponent<{
  onClose: () => void;
  confirmRemove: () => void;
}> = ({ onClose, confirmRemove }) => {
  return (
    <>
      <Modal.Header className='px-6 py-3'>
        <Modal.Title>Do you want to remove yourself from the team?</Modal.Title>
      </Modal.Header>
      <Modal.Footer>
        <div className='w-full flex justify-between'>
          <Button
            className='text-color-cancel'
            onClick={(): void => {
              onClose();
            }}
          >
            Don't remove
          </Button>
          <Button.rounded
            className='px-8'
            onClick={(): void => {
              confirmRemove();
            }}
          >
            OK
          </Button.rounded>
        </div>
      </Modal.Footer>
    </>
  );
};

export const WarningLastAdmin: FunctionComponent<{
  onClose: () => void;
  message?: ReactElement;
}> = ({ onClose, message }) => {
  return (
    <>
      <Modal.Body>
        <Modal.StickyButtonHeader>
          <Modal.Close onClick={onClose} />
        </Modal.StickyButtonHeader>
        <Modal.Title className='mt-1e'>
          {message || 'You must maintain at least one Admin in a team at all times.'}
        </Modal.Title>
      </Modal.Body>
    </>
  );
};

const RemoveMemberModal: FunctionComponent<RemoveModalProps> = ({ me, member, users, onClose }) => {
  const [isRemoveYourself, setIsRemoveYourself] = React.useState(false);
  const admins = users.filter(
    (u) => AdminRoles.includes(u.role as string as MyRole) && u.id !== member.id
  );
  const confirmRemoveYourself = (): void => {
    setIsRemoveYourself(true);
  };

  const [removeUserMutation] = useRemoveUserMutation();

  const onRemoveUser = async (data: RemoveUserInput): Promise<void> => {
    const removeUserResult = await removeUserMutation({
      variables: { ...data },
      refetchQueries: [TEAM_USERS_REFETCH, { query: GET_SETS_STATUS }],
    });
    if (removeUserResult && removeUserResult.data && removeUserResult.data.removeUser) {
      onClose();
    }
  };

  return (
    <Modal
      className='remove-member-modal'
      css={css`
        overflow: visible;
      `}
    >
      {me.id === member.id && !isRemoveYourself ? (
        <WarningYourSelf onClose={onClose} confirmRemove={confirmRemoveYourself} />
      ) : admins.length === 0 && AdminRoles.includes(member.role as string as MyRole) ? (
        <WarningLastAdmin onClose={onClose} />
      ) : (
        <SelectReplacementUser
          me={me}
          member={member}
          users={users}
          onClose={onClose}
          onRemoveUser={onRemoveUser}
        />
      )}
    </Modal>
  );
};

export default RemoveMemberModal;
